import { UnitStatusSearchForm, UnitStatusSearchResponse } from '@mark43/cad-api';
import { observer } from 'mobx-react-lite';
import { Box } from '@cad-arc';
import { UnitStatusSearchResultsHeader } from '../unit-status-search-results-header';
import { ErrorBoundary } from '../../monitoring/error-boundary';
import { useExportUnitStatusSearchResultsMutation } from '../use-export-unit-status-search-results-mutation';
import { useObservableSearchResultSelectionStore } from '../../search-windows/use-observable-search-results-selection-store';
import {
    UnitStatusSearchResultsPaginationProvider,
    useUnitStatusSearchResultsPaginationContext,
} from '../unit-status-search-results-pagination-provider';
import { useSearchUnitStatusesQuery } from '../use-search-unit-statuses-query';
import { useExecuteSavedUnitStatusSearchQuery } from '../use-execute-saved-unit-status-search-query';
import { SearchResults } from '../../search-windows/search-results';
import { UnitStatusSearchResultsTable } from '../unit-status-search-results-table';
import { UnitStatusSearchSortProvider } from '../unit-status-search-sort-provider';
import {
    FR_TOTAL_NON_RESULTS_HEIGHT_PX,
    SEARCH_RESULTS_EXPORTS_FOOTER_HEIGHT_PX,
} from '../../search-windows/constants';
import { useCallback } from 'react';
import { FileExportType } from '../../event-search/types';

interface UnitStatusSearchResultsProps {
    searchData: UnitStatusSearchForm;
    searchTab: number;
    setSavedSearchId: (savedSearchId?: number) => void;
    closeSearch: () => void;
    savedSearchId?: number;
}

const _UnitStatusSearchResults = observer(function _UnitStatusSearchResults({
    savedSearchId,
    searchData,
    searchTab,
    setSavedSearchId,
    closeSearch,
}: UnitStatusSearchResultsProps) {
    const pagination = useUnitStatusSearchResultsPaginationContext();

    const searchUnitStatusesQuery = useSearchUnitStatusesQuery(
        searchTab,
        searchData,
        savedSearchId
    );
    const executeSavedSearchQuery = useExecuteSavedUnitStatusSearchQuery(searchTab, savedSearchId);

    const unitStatusSearchResponse = searchUnitStatusesQuery.data ?? executeSavedSearchQuery.data;
    const totalCount = unitStatusSearchResponse?.totalCount ?? 0;
    const results = (
        unitStatusSearchResponse as UnitStatusSearchResponse
    )?.unitStatusSearchRows.map((row) => ({
        id: row.unitStatusRmsEventId,
    }));

    const selection = useObservableSearchResultSelectionStore(results ?? [], totalCount);

    const exportUnitStatusSearchResultsMutation = useExportUnitStatusSearchResultsMutation();

    const showExportsFooter = results && selection.anySelected;
    const totalUsedPageHeight = showExportsFooter
        ? FR_TOTAL_NON_RESULTS_HEIGHT_PX + SEARCH_RESULTS_EXPORTS_FOOTER_HEIGHT_PX
        : FR_TOTAL_NON_RESULTS_HEIGHT_PX;
    const hasResults = (results?.length ?? 0) > 0;

    const handleFileExport = useCallback(
        ({ entityIds }: { type?: FileExportType; entityIds?: number[] }) => {
            exportUnitStatusSearchResultsMutation.mutate({
                unitStatusRmsEventIds: entityIds ?? [],
                unitStatusSearchForm: entityIds?.length ? undefined : searchData,
            });
        },
        [exportUnitStatusSearchResultsMutation, searchData]
    );

    return (
        <ErrorBoundary
            name="UNIT_SEARCH_RESULTS"
            actions={[{ children: 'Return to search form', onClick: closeSearch }]}
        >
            <Box
                h={`calc(100% - ${totalUsedPageHeight}px)`}
                w="100%"
                overflowY="auto"
                style={{ marginTop: 0 }}
            >
                <SearchResults
                    header={
                        <UnitStatusSearchResultsHeader
                            hasResults={hasResults}
                            unitStatusSearchForm={searchData}
                            savedSearchId={savedSearchId}
                            setSavedSearchId={setSavedSearchId}
                        />
                    }
                    searchQuery={searchUnitStatusesQuery}
                    executeSavedSearchQuery={executeSavedSearchQuery}
                    results={results}
                    selection={selection}
                    pagination={pagination}
                    onFileExport={handleFileExport}
                    isExportLoading={exportUnitStatusSearchResultsMutation.isLoading}
                    isEventSearch={false}
                >
                    <UnitStatusSearchResultsTable
                        unitStatusSearchResponse={unitStatusSearchResponse}
                        selection={selection}
                        customStyle={{ backgroundColor: 'var(--arc-colors-surface-background)' }}
                        searchData={searchData}
                    />
                </SearchResults>
            </Box>
        </ErrorBoundary>
    );
});

export function UnitStatusSearchResults(props: UnitStatusSearchResultsProps) {
    return (
        <UnitStatusSearchResultsPaginationProvider>
            <UnitStatusSearchSortProvider>
                <_UnitStatusSearchResults {...props} />
            </UnitStatusSearchSortProvider>
        </UnitStatusSearchResultsPaginationProvider>
    );
}
